import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CheckCircle, WarningCircle, CircleNotch, ArrowsClockwise, Eye, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const STATUS = {
  pending_approval: { label: "Pending approval", className: "bg-[#FEF0C7] text-[#93370D] border-[#FEDF89]" },
  processing: { label: "Posting to SAP...", className: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" },
  completed: { label: "Completed", className: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  partially_posted: { label: "Partially posted", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  stock_changed: { label: "Stock changed", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  rejected: { label: "Rejected", className: "bg-[#FEE4E2] text-[#B42318] border-[#FECDCA]" },
  failed: { label: "Failed", className: "bg-[#FEE4E2] text-[#B42318] border-[#FECDCA]" },
  verified_no_adjustment: { label: "Verified, no adjustment", className: "bg-[#F2F4F7] text-[#475467] border-[#E4E7EC]" },
};

export const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 }));

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, className: "bg-[#F2F4F7] text-[#475467] border-[#E4E7EC]" };
  return <Badge variant="outline" className={`text-[10px] ${s.className}`} data-testid={`inv-adj-status-${status}`}>{s.label}</Badge>;
};

export const ACTION_LABEL = { issue: "Goods Issue", return: "Goods Return", none: "No Action" };
export const CATEGORY_LABEL = { bom: "BOM", consumable: "Consumable" };

/** The counted lines exactly as they were snapshotted - shared by the review
    queue and the read-only Physical Inventory Audit tab. */
export const CountLinesTable = ({ lines, testPrefix = "inv-adj-line" }) => (
  <div className="border border-[#E4E7EC] rounded-lg overflow-x-auto w-full min-w-0">
    <Table className="min-w-[760px]">
      <TableHeader>
        <TableRow className="bg-[#F9FAFB]">
          <TableHead className="text-xs">Item ID</TableHead>
          <TableHead className="text-xs">Item Name</TableHead>
          <TableHead className="text-xs">Warehouse</TableHead>
          <TableHead className="text-xs text-right">Current</TableHead>
          <TableHead className="text-xs text-right">Physical</TableHead>
          <TableHead className="text-xs text-right">Difference</TableHead>
          <TableHead className="text-xs">SAP Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((l) => (
          <TableRow key={`${l.product_id}-${l.warehouse_id}`} data-testid={`${testPrefix}-${l.product_id}-${l.warehouse_id}`}>
            <TableCell className="font-mono text-xs font-bold text-[#101828]">{l.product_id}</TableCell>
            <TableCell className="text-xs text-[#344054] max-w-[220px] truncate" title={l.description}>
              {l.description || "—"}
              {l.counted_by && <span className="block text-[10px] text-[#98A2B3]">counted by {l.counted_by}</span>}
            </TableCell>
            <TableCell className="text-xs font-mono text-[#344054]">{l.warehouse_id}</TableCell>
            <TableCell className="text-xs text-right tabular-nums">{fmt(l.current_qty)} {l.uom}</TableCell>
            <TableCell className="text-xs text-right tabular-nums">{fmt(l.physical_qty)} {l.uom}</TableCell>
            <TableCell className={`text-xs text-right tabular-nums font-bold ${l.difference < 0 ? "text-[#B42318]" : l.difference > 0 ? "text-[#027A48]" : "text-[#667085]"}`}>
              {l.difference > 0 ? `+${fmt(l.difference)}` : fmt(l.difference)}
            </TableCell>
            <TableCell className="text-xs text-[#344054]">
              {ACTION_LABEL[l.action] || l.action}
              {l.action !== "none" ? ` ${fmt(l.adjustment_qty)}` : ""}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

/** Only counts still waiting on a decision live here - everything that has
    been settled is kept in the Physical Inventory Audit tab (user, 22 Oct). */
const OPEN_STATUSES = ["pending_approval", "stock_changed", "failed", "partially_posted", "processing"];
/** A count the administrator can still decide on - the same set the single
    Approve button allows, so a selection can never include a posting in flight. */
const DECIDABLE = ["pending_approval", "stock_changed", "failed", "partially_posted"];
const BULK_MAX = 30;

export const InventoryAdjustmentRequests = ({ onPendingCount }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmPost, setConfirmPost] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selected, setSelected] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [bulk, setBulk] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/physical-inventory/requests`, { params: { scope: "all" } });
      setRequests((data.requests || []).filter((r) => OPEN_STATUSES.includes(r.status)));
      onPendingCount?.(data.pending_count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load inventory adjustment requests");
    } finally {
      setLoading(false);
    }
  }, [onPendingCount]);

  useEffect(() => { load(); }, [load]);

  // Settles every count stuck at "Posting to SAP..." in one SAP pass.
  const checkWithSap = async () => {
    setBusy("sap-check");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/recover-pending`);
      if (data.recovered.length) {
        toast.success(`${data.recovered.length} movement(s) found in SAP: ${data.recovered.map((r) => r.sap_gac_id).join(", ")}`);
      } else if (data.not_in_sap.length) {
        toast.warning(`${data.not_in_sap.length} movement(s) never reached SAP - they can be posted again`);
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.success("Every movement is already settled with SAP");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAP could not be reached for this check");
    } finally {
      setBusy(null);
    }
  };

  const openDetail = async (requestId) => {
    try {
      const { data } = await axios.get(`${API}/physical-inventory/requests/${requestId}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open the request");
    }
  };

  const refreshDetail = async (requestId, quiet = false) => {
    setBusy("refresh");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/requests/${requestId}/refresh-status`);
      setDetail(data);
      load();
      if (!quiet) toast.success(`Status: ${(STATUS[data.status] || {}).label || data.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh the status");
    } finally {
      setBusy("");
    }
  };

  const approve = async () => {
    const requestId = detail.request_id;
    setBusy("approve");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/requests/${requestId}/approve`);
      setDetail(data);
      setConfirmPost(false);
      load();
      if (data.status === "stock_changed") toast.error(data.error || "SAP stock changed since the count - nothing was posted");
      else if (data.status === "completed") toast.success("Adjustment posted to SAP");
      else if (data.status === "failed") toast.error(data.error || "SAP rejected the adjustment");
      else toast.info("Sent to SAP - refresh in a moment for the document number");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post the adjustment");
      load();
    } finally {
      setBusy("");
    }
  };

  const reject = async () => {
    setBusy("reject");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/requests/${detail.request_id}/reject`, { reason: rejectReason });
      setDetail(data);
      setRejecting(false);
      setRejectReason("");
      load();
      toast.success("Request rejected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject the request");
    } finally {
      setBusy("");
    }
  };

  const selectableIds = requests.filter((r) => DECIDABLE.includes(r.status)).map((r) => r.request_id);
  const toggleOne = (requestId) =>
    setSelected((prev) => (prev.includes(requestId) ? prev.filter((id) => id !== requestId) : [...prev, requestId].slice(0, BULK_MAX)));
  const toggleAll = () =>
    setSelected((prev) => (prev.length ? [] : selectableIds.slice(0, BULK_MAX)));

  /** Approve everything that is ticked, one request at a time on the server
      (each is a real SAP posting), polling the job until it finishes. */
  const approveSelected = async () => {
    setBusy("bulk");
    setBulk({ total: selected.length, done: 0, results: [] });
    try {
      const { data } = await axios.post(`${API}/physical-inventory/requests/approve-bulk`, { request_ids: selected });
      let job = { status: "running", total: data.total, done: 0, results: [] };
      while (job.status === "running") {
        await new Promise((r) => setTimeout(r, 2500));
        const res = await axios.get(`${API}/physical-inventory/requests/approve-bulk/${data.job_id}`);
        job = res.data;
        setBulk({ total: job.total, done: job.done, results: job.results || [] });
      }
      const posted = (job.results || []).filter((r) => ["completed", "processing", "partially_posted"].includes(r.status));
      if (posted.length === job.total) toast.success(`${posted.length} adjustment(s) posted to SAP`);
      else toast.error(`${posted.length} of ${job.total} posted - open the ones left for the reason`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve the selected requests");
    } finally {
      setBusy("");
      setConfirmBulk(false);
    }
  };

  /** Clears ticked counts out of the queue. Nothing is deleted - they keep
      their status and stay in Physical Inventory Audit for ever. */
  const removeSelected = async () => {
    setBusy("remove");
    try {
      const { data } = await axios.post(`${API}/physical-inventory/requests/remove-from-queue`, { request_ids: selected });
      if (data.removed?.length) toast.success(`${data.removed.length} count(s) removed from the queue - still in Physical Inventory Audit`);
      if (data.skipped?.length) toast.warning(data.skipped.map((s) => `${s.request_id}: ${s.reason}`).join(" · "));
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove the selected counts");
    } finally {
      setBusy("");
      setConfirmRemove(false);
    }
  };

  const issueLines = (detail?.lines || []).filter((l) => l.action === "issue");
  const returnLines = (detail?.lines || []).filter((l) => l.action === "return");
  const canDecide = ["pending_approval", "stock_changed", "failed", "partially_posted"].includes(detail?.status);

  return (
    <div className="space-y-4" data-testid="inventory-adjustment-requests">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-[#667085] max-w-3xl">
          Physical inventory counted on the floor and still waiting on a decision. Shortages post as a Consumption
          (Goods Issue), excesses as a Return from Cost Center - both through the same SAP services as the New
          Submission tab, against the site's cost centre. Settled counts are kept in <strong>Physical Inventory Audit</strong>.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {!!selected.length && !confirmBulk && (
            confirmRemove ? (
              <div className="flex items-center gap-2 bg-[#FEF3F2] border border-[#FECDCA] rounded-md px-2 py-1.5" data-testid="inv-adj-remove-confirm">
                <span className="text-xs font-bold text-[#B42318]">
                  Remove {selected.length} count{selected.length > 1 ? "s" : ""} from this queue?
                </span>
                <Button size="sm" onClick={removeSelected} disabled={busy === "remove"}
                  className="bg-[#B42318] hover:bg-[#912018]" data-testid="inv-adj-remove-confirm-btn">
                  {busy === "remove" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <Trash size={14} weight="fill" className="mr-1.5" />}
                  Yes, remove
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmRemove(false)} data-testid="inv-adj-remove-cancel-btn">
                  Keep them
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirmRemove(true)} disabled={busy === "remove" || busy === "bulk"}
                className="text-[#B42318] border-[#FECDCA] hover:bg-[#FEF3F2]" data-testid="inv-adj-remove-btn">
                <Trash size={14} className="mr-1.5" />
                Remove {selected.length} from queue
              </Button>
            )
          )}
          {!!selected.length && !confirmRemove && (
            confirmBulk ? (
              <div className="flex items-center gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-md px-2 py-1.5" data-testid="inv-adj-bulk-confirm">
                <span className="text-xs font-bold text-[#B54708]">
                  Post {selected.length} count{selected.length > 1 ? "s" : ""} to SAP now?
                </span>
                <Button size="sm" onClick={approveSelected} disabled={busy === "bulk"}
                  className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="inv-adj-bulk-confirm-btn">
                  {busy === "bulk" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} weight="fill" className="mr-1.5" />}
                  Yes, post them
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmBulk(false)} data-testid="inv-adj-bulk-cancel-btn">
                  Not yet
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => setConfirmBulk(true)} disabled={busy === "bulk"}
                className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="inv-adj-bulk-approve-btn">
                <CheckCircle size={14} weight="fill" className="mr-1.5" />
                Approve &amp; Post {selected.length} selected
              </Button>
            )
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading || busy === "bulk"} data-testid="inv-adj-refresh-btn">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {!!requests.filter((r) => r.status === "processing").length && (
        <div className="border border-[#B2DDFF] bg-[#EFF8FF] rounded-xl px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid="inv-adj-still-posting-banner">
          <div className="flex-1 min-w-[240px]">
            <p className="text-xs font-bold text-[#175CD3]">
              {requests.filter((r) => r.status === "processing").length} count(s) still show &quot;Posting to SAP&quot;
            </p>
            <p className="text-[11px] text-[#175CD3]">
              One check asks SAP about all of them at once and marks each posted or not posted - no opening them row by row.
            </p>
          </div>
          <Button size="sm" onClick={checkWithSap} disabled={busy === "sap-check"}
            className="bg-[#175CD3] hover:bg-[#1849A9]" data-testid="inv-adj-check-sap-btn">
            {busy === "sap-check" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" />}
            Check movement status in SAP
          </Button>
        </div>
      )}

      {!!requests.filter((r) => r.status === "failed").length && (
        <div className="border border-[#FECDCA] bg-[#FEF3F2] rounded-xl px-4 py-3" data-testid="inv-adj-failed-banner">
          <p className="text-xs font-bold text-[#B42318]">
            {requests.filter((r) => r.status === "failed").length} count(s) SAP rejected are still listed here
          </p>
          <p className="text-[11px] text-[#B42318]">
            A failed posting stays in this queue on purpose, so it can be tried again or rejected - it is not a duplicate
            of the Physical Inventory Audit entry, it is the same count still waiting on a decision. Once it is dealt
            with, tick it and press <strong>Remove from queue</strong>; the count, its SAP error and its tags stay in
            Physical Inventory Audit for ever.
          </p>
        </div>
      )}

      {bulk && (
        <div className="bg-[#F9FAFB] border border-[#E4E7EC] rounded-xl p-3 text-xs space-y-1" data-testid="inv-adj-bulk-progress">
          <p className="font-bold text-[#101828]">
            Posting {bulk.done} of {bulk.total} to SAP{busy === "bulk" ? "..." : " - finished"}
          </p>
          {(bulk.results || []).map((r) => (
            <p key={r.request_id} className="font-mono flex items-center gap-2" data-testid={`inv-adj-bulk-result-${r.request_id}`}>
              <span className="text-[#344054]">{r.request_id}</span>
              <StatusBadge status={r.status} />
              {r.error && <span className="text-[#B42318] font-sans">{r.error}</span>}
            </p>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow className="bg-[#F9FAFB]">
              <TableHead className="text-xs w-10">
                <Checkbox checked={!!selected.length && selected.length === Math.min(selectableIds.length, BULK_MAX)}
                  onCheckedChange={toggleAll} disabled={!selectableIds.length || busy === "bulk"}
                  aria-label="Select all" data-testid="inv-adj-select-all" />
              </TableHead>
              <TableHead className="text-xs">Request ID</TableHead>
              <TableHead className="text-xs">Date</TableHead>
              <TableHead className="text-xs">Site</TableHead>
              <TableHead className="text-xs">Type</TableHead>
              <TableHead className="text-xs">Requested By</TableHead>
              <TableHead className="text-xs text-right">Items</TableHead>
              <TableHead className="text-xs text-right">Shortage</TableHead>
              <TableHead className="text-xs text-right">Excess</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={11} className="text-sm text-[#667085] py-6 text-center" data-testid="inv-adj-loading">Loading...</TableCell></TableRow>}
            {!loading && !requests.length && <TableRow><TableCell colSpan={11} className="text-sm text-[#667085] py-6 text-center" data-testid="inv-adj-empty">Nothing waiting on a decision. Past counts are in the Physical Inventory Audit tab.</TableCell></TableRow>}
            {requests.map((r) => (
              <TableRow key={r.request_id} data-testid={`inv-adj-row-${r.request_id}`}>
                <TableCell>
                  <Checkbox checked={selected.includes(r.request_id)} onCheckedChange={() => toggleOne(r.request_id)}
                    disabled={!DECIDABLE.includes(r.status) || busy === "bulk"}
                    aria-label={`Select ${r.request_id}`} data-testid={`inv-adj-select-${r.request_id}`} />
                </TableCell>
                <TableCell className="font-mono text-xs font-bold text-[#101828]">{r.request_id}</TableCell>
                <TableCell className="text-xs text-[#667085]">{new Date(r.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-xs text-[#344054]">{r.site_id}</TableCell>
                <TableCell className="text-xs text-[#344054]">{CATEGORY_LABEL[r.category] || r.category}</TableCell>
                <TableCell className="text-xs text-[#344054]">{r.requester_name}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{r.summary?.total_items ?? 0}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-[#B42318]">{r.summary?.shortage_items ?? 0}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-[#027A48]">{r.summary?.excess_items ?? 0}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openDetail(r.request_id)} data-testid={`inv-adj-view-${r.request_id}`}>
                    <Eye size={12} className="mr-1" /> View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setConfirmPost(false); setRejecting(false); } }}>
        <DialogContent className="sm:max-w-[900px] w-[calc(100vw-1.5rem)] max-h-[88vh] overflow-y-auto" data-testid="inv-adj-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <span className="font-mono">{detail?.request_id}</span>
              {detail && <StatusBadge status={detail.status} />}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 min-w-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-xs min-w-0">
                <span className="text-[#667085]">Site</span><span className="font-bold text-[#101828]">{detail.site_id}</span>
                <span className="text-[#667085]">Cost Center</span><span className="font-bold text-[#101828] font-mono">{detail.cost_center_id}</span>
                <span className="text-[#667085]">Type</span><span className="font-bold text-[#101828]">{CATEGORY_LABEL[detail.category] || detail.category}</span>
                <span className="text-[#667085]">Requested by</span><span className="font-bold text-[#101828]">{detail.requester_name}</span>
                <span className="text-[#667085]">Goods Issue qty</span><span className="font-bold text-[#B42318]" data-testid="inv-adj-issue-qty">{fmt(detail.summary?.total_issue_qty)}</span>
                <span className="text-[#667085]">Goods Return qty</span><span className="font-bold text-[#027A48]" data-testid="inv-adj-return-qty">{fmt(detail.summary?.total_return_qty)}</span>
                <span className="text-[#667085]">Counted on</span><span className="font-bold text-[#101828]">{new Date(detail.created_at).toLocaleString()}</span>
                <span className="text-[#667085]">Remarks</span><span className="font-bold text-[#101828]">{detail.remarks || "—"}</span>
              </div>

              {detail.error && (
                <div className="flex items-start gap-2 bg-[#FEF3F2] border border-[#FECDCA] rounded-lg px-3 py-2 text-xs text-[#B42318]" data-testid="inv-adj-error">
                  <WarningCircle size={15} weight="fill" className="shrink-0 mt-0.5" /><span>{detail.error}</span>
                </div>
              )}

              {!!detail.stock_changes?.length && (
                <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-lg px-3 py-2 text-xs text-[#B54708]" data-testid="inv-adj-stock-changes">
                  <p className="font-bold mb-1">SAP stock moved since this count - nothing was posted:</p>
                  {detail.stock_changes.map((c) => (
                    <p key={`${c.product_id}-${c.warehouse_id}`} className="font-mono">
                      {c.product_id} @ {c.warehouse_id}: counted against {fmt(c.counted_against)}, SAP now {fmt(c.current_qty)}
                    </p>
                  ))}
                </div>
              )}

              {!!detail.postings?.length && (
                <div className="border border-[#E4E7EC] rounded-lg divide-y divide-[#F2F4F7]" data-testid="inv-adj-postings">
                  {detail.postings.map((p) => (
                    <div key={p.group} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs">
                      <span className="font-bold text-[#101828]">{p.group === "issue" ? "Goods Issue" : "Goods Return"} · {p.line_count} line(s) · {fmt(p.quantity)}</span>
                      <span className="flex items-center gap-2">
                        {p.sap_gac_id && <span className="font-mono text-[#027A48]" data-testid={`inv-adj-doc-${p.group}`}>SAP doc {p.sap_gac_id}</span>}
                        {p.error && <span className="text-[#B42318]">{p.error}</span>}
                        <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <CountLinesTable lines={detail.lines} />

              {rejecting && (
                <div>
                  <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2}
                    placeholder="Why is this request rejected?" data-testid="inv-adj-reject-reason" />
                </div>
              )}

              {confirmPost && (
                <div className="bg-[#F9FAFB] border border-[#E4E7EC] rounded-lg p-3 text-xs text-[#344054]" data-testid="inv-adj-confirm-post">
                  <p className="font-bold text-[#101828] mb-1">Post this adjustment to SAP?</p>
                  <p>Goods Issue: {issueLines.length} line(s), {fmt(detail.summary?.total_issue_qty)} total.
                     Goods Return: {returnLines.length} line(s), {fmt(detail.summary?.total_return_qty)} total.</p>
                  <p className="mt-1">Live stock is re-checked first - if anything has moved since the count, nothing is posted.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {detail?.postings?.length ? (
              <Button variant="outline" className="w-full sm:w-auto" onClick={() => refreshDetail(detail.request_id)} disabled={busy === "refresh"} data-testid="inv-adj-refresh-status-btn">
                {busy === "refresh" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" />} Refresh SAP status
              </Button>
            ) : null}
            {confirmPost && (
              <Button variant="outline" className="w-full sm:w-auto" onClick={() => setConfirmPost(false)} data-testid="inv-adj-back-btn">
                Back
              </Button>
            )}
            {canDecide && !confirmPost && (
              <Button variant="outline" className="w-full sm:w-auto text-[#B42318] border-[#FECDCA]"
                onClick={() => (rejecting ? reject() : setRejecting(true))}
                disabled={busy === "reject" || (rejecting && !rejectReason.trim())} data-testid="inv-adj-reject-btn">
                {rejecting ? "Confirm rejection" : "Reject Request"}
              </Button>
            )}
            {canDecide && !rejecting && (
              confirmPost ? (
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto" onClick={() => setConfirmPost(false)}
                    disabled={busy === "approve"} data-testid="inv-adj-approve-cancel-btn">
                    Not yet
                  </Button>
                  <Button onClick={approve} disabled={busy === "approve"}
                    className="w-full sm:w-auto bg-[#B54708] hover:bg-[#92370A]" data-testid="inv-adj-approve-confirm-btn">
                    {busy === "approve" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} weight="fill" className="mr-1.5" />}
                    Yes, post it to SAP
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setConfirmPost(true)} disabled={busy === "approve"}
                  className="w-full sm:w-auto bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="inv-adj-approve-btn">
                  <CheckCircle size={14} weight="fill" className="mr-1.5" />
                  Approve &amp; Post Adjustment
                </Button>
              )
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
